<template>
  <div class="yard-tasks-assignee-select-wrapper d-flex flex-column gap-2">
    <v-autocomplete
      data-testid="yard-tasks-assignee-select"
      class="yard-tasks-assignee-select"
      hide-details
      color="primary"
      density="compact"
      :item-title="getFullName"
      item-value="id"
      variant="outlined"
      :clearable="clearable"
      :disabled="disabled"
      :items="users"
      :model-value="modelValue"
      :multiple="multiple"
      :placeholder="placeholder"
      :return-object="false"
      @update:model-value="handleValueChange">
      <template #no-data>
        <v-list-item :title="t('yard.messages.noUsersAvailable')"></v-list-item>
      </template>

      <template #selection="{ index }">
        <span v-if="index === 0" class="typography-body-md">
          {{ getRenderedSelectedValue() }}
        </span>
      </template>

      <template #prepend-item v-if="multiple && users.length > 0">
        <v-list-item title="All users" ripple @click="toggleSelectAll" class="typography-body-md">
          <template v-slot:prepend>
            <v-icon>{{ selectAllIcon }}</v-icon>
          </template>
        </v-list-item>
        <v-divider class="mt-2"></v-divider>
      </template>

      <template #item="{ item, props }">
        <v-list-item ripple v-bind="props" class="typography-body-md" :title="item.title">
          <template v-slot:prepend v-if="multiple">
            <v-icon>{{ getItemIcon(item) }}</v-icon>
          </template>

          <template v-slot:append v-if="multiple">
            <v-btn
              variant="text"
              size="small"
              class="text-none typography-button-sm ml-auto"
              @click="selectOnly($event, item)">
              {{ t('global.basicTerms.only') }}
            </v-btn>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>

    <!-- slot for any addition content.-->
    <slot
      v-if="modelValue"
      name="selected-users"
      :selected-user="getSelectedUsers()"
      :selected-users="getSelectedUsers()"></slot>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onBeforeMount, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import { useEventHub, useSockets, useNotify } from '@/composables';

import userService from '@satellite/services/user-service';

export default defineComponent({
  name: 'YardTasksAssigneeSelect',
  emits: ['update:modelValue', 'input'],
  props: {
    modelValue: {
      type: [Array, String, Number],
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const sockets = useSockets();
    const eventHub = useEventHub();
    const notify = useNotify();

    const users = ref([]);

    const handleValueChange = newValue => {
      emit('update:modelValue', newValue);
      emit('input', newValue);
    };

    const getSelectedUsers = () => {
      if (!props.modelValue) {
        return null;
      }

      if (props.multiple) {
        const selectedAreas = Array.isArray(props.modelValue)
          ? props.modelValue
          : [props.modelValue];
        return selectedAreas.map(user => users.value.find(u => u.id === user));
      }

      return users.value.find(user => user.id === props.modelValue);
    };

    const getFullName = item => {
      if (!item) return null;
      return `${item.firstName || ''} ${item.lastName || ''}`.trim();
    };

    const getRenderedSelectedValue = () => {
      const selectedUsers = getSelectedUsers();

      if (!selectedUsers) {
        return '';
      }

      if (props.multiple) {
        const selectedCount = selectedUsers.length;
        return `${selectedCount} ${t('global.basicTerms.of')} ${users.value.length} ${t(
          'forms.common.terms.selected'
        )}`;
      }

      return getFullName(selectedUsers);
    };

    const isAllSelected = computed(() => {
      if (!props.multiple) return false;
      const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
      return selected.length === users.value.length && users.value.length > 0;
    });

    const selectAllIcon = computed(() => {
      if (isAllSelected.value) {
        return 'mdi-close-box';
      }
      const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
      return selected.length > 0 ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline';
    });

    const toggleSelectAll = () => {
      if (!props.multiple) {
        return;
      }

      const newValue = isAllSelected.value ? [] : users.value.map(user => user.id);
      handleValueChange(newValue);
    };

    const getItemIcon = item => {
      if (!props.multiple) {
        return '';
      }

      const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
      return selected.includes(item.value) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline';
    };

    const selectOnly = (event, item) => {
      event.stopPropagation();

      if (!props.multiple) {
        return;
      }

      handleValueChange([item.value]);
    };

    const fetchUsers = async () => {
      try {
        // TODO: should we filter by email-verified?
        const fetchedUsers = await userService.getUsers({
          s: { role: 'role_yard_driver' }
        });

        users.value = fetchedUsers;
      } catch (error) {
        users.value = [];

        // TODO: should we retry a couple of times before failing?
        notify(t('yard.messages.failedToFetchUsers'), 'error');
      }
    };

    onBeforeMount(async () => {
      await fetchUsers();

      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$on(`${operation}-User`, fetchUsers);
      });
    });

    onBeforeUnmount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$off(`${operation}-User`, fetchUsers);
      });
    });

    return {
      t,
      users,
      handleValueChange,
      getSelectedUsers,
      getRenderedSelectedValue,
      getFullName,
      isAllSelected,
      selectAllIcon,
      toggleSelectAll,
      getItemIcon,
      selectOnly
    };
  }
});
</script>

<style lang="scss" scoped>
.yard-tasks-assignee-select {
  min-inline-size: 220px;
}
</style>
